import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Card,
  CardContent
} from '@mui/material';

const WeatherDataForm = ({ 
  weatherData, 
  onChange, 
  title = "Weather Conditions",
  compact = false 
}) => {
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    onChange({
      ...weatherData,
      [field]: value === '' ? null : value
    });
  };

  const handleNumericChange = (field) => (event) => {
    const value = event.target.value;
    onChange({
      ...weatherData,
      [field]: value === '' ? null : parseFloat(value)
    });
  };

  const handleIntegerChange = (field) => (event) => {
    const value = event.target.value;
    onChange({
      ...weatherData,
      [field]: value === '' ? null : parseInt(value)
    });
  };

  const visibilityOptions = [
    { value: 'unlimited', label: 'Unlimited' },
    { value: '10km', label: '10 km' },
    { value: '5km', label: '5 km' },
    { value: '2km', label: '2 km' },
    { value: '1km', label: '1 km' },
    { value: '500m', label: '500 m' },
    { value: '200m', label: '200 m' },
    { value: '100m', label: '100 m' },
    { value: '50m', label: '50 m' },
    { value: '10m', label: '10 m' },
    { value: '5m', label: '5 m' },
    { value: '1m', label: '1 m' }
  ];

  const windOptions = [
    { value: 'calm', label: 'Calm' },
    { value: 'light', label: 'Light Breeze' },
    { value: 'moderate', label: 'Moderate Breeze' },
    { value: 'fresh', label: 'Fresh Breeze' },
    { value: 'strong', label: 'Strong Breeze' },
    { value: 'gale', label: 'Gale' },
    { value: 'storm', label: 'Storm' },
    { value: 'extreme', label: 'Extreme' }
  ];

  const precipitationOptions = [
    { value: 'none', label: 'None' },
    { value: 'snow', label: 'Snow' },
    { value: 'light_snow', label: 'Light Snow' },
    { value: 'heavy_snow', label: 'Heavy Snow' },
    { value: 'rain', label: 'Rain' },
    { value: 'light_rain', label: 'Light Rain' },
    { value: 'heavy_rain', label: 'Heavy Rain' },
    { value: 'sleet', label: 'Sleet' },
    { value: 'hail', label: 'Hail' }
  ];

  const artificialSnowOptions = [
    { value: 'A1', label: 'A1 (Falling new)' },
    { value: 'A2', label: 'A2 (New)' },
    { value: 'A3', label: 'A3 (Irregularly decomposed new snow)' },
    { value: 'A4', label: 'A4 (Irregularly decomposed transformed snow)' },
    { value: 'A5', label: 'A5 (Transformed)' }
  ];

  const naturalSnowOptions = [
    { value: 'FN', label: 'FN (Falling New)' },
    { value: 'NS', label: 'NS (New)' },
    { value: 'IN', label: 'IN (Irregularly decomposed new)' },
    { value: 'IT', label: 'IT (Irregularly decomposed transformed snow)' },
    { value: 'TR', label: 'TR (Transformed)' }
  ];

  const grainSizeOptions = [
    { value: 'G0', label: 'G0 (0.0-0.2mm - Extra fine)' },
    { value: 'G1', label: 'G1 (0.2-0.5mm - Very fine)' },
    { value: 'G2', label: 'G2 (0.5-1.0mm - Fine)' },
    { value: 'G3', label: 'G3 (2.0-4.0mm - Coarse)' },
    { value: 'G5', label: 'G5 (>4.0mm - Very coarse)' }
  ];

  const snowHumidityOptions = [
    { value: 'DS', label: 'DS (Dry)' },
    { value: 'W1', label: 'W1 (Moist)' },
    { value: 'W2', label: 'W2 (Wet)' },
    { value: 'W3', label: 'W3 (Very wet)' },
    { value: 'W4', label: 'W4 (Slush)' }
  ];

  const trackHardnessOptions = [
    { value: 'H1', label: 'H1 (Very soft)' },
    { value: 'H2', label: 'H2 (Soft)' },
    { value: 'H3', label: 'H3 (Medium hard)' },
    { value: 'H4', label: 'H4 (Hard)' },
    { value: 'H5', label: 'H5 (Very hard)' },
    { value: 'H6', label: 'H6 (Ice)' }
  ];

  const trackShininessOptions = [
    { value: 'T0', label: 'T0 (Matte)' },
    { value: 'T1', label: 'T1 (Partly shiny)' },
    { value: 'T2', label: 'T2 (Shiny)' }
  ];

  const trackDirtinessOptions = [
    { value: 'D0', label: 'D0 (Clean)' },
    { value: 'D1', label: 'D1 (Partly dirty)' },
    { value: 'D2', label: 'D2 (Dirty)' }
  ];

  return (
    <Paper sx={{ p: compact ? 2 : 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Temperature Section */}
        <Grid item xs={12} width="100%">
          <Card variant="outlined" sx={{ mb: 2, width: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Temperature
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Snow Temperature (°C)"
                    type="number"
                    value={weatherData.snow_temperature_c ?? ''}
                    onChange={handleNumericChange('snow_temperature_c')}
                    inputProps={{ step: "0.1" }}
                    size={compact ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Air Temperature (°C)"
                    type="number"
                    value={weatherData.air_temperature_c ?? ''}
                    onChange={handleNumericChange('air_temperature_c')}
                    inputProps={{ step: "0.1" }}
                    size={compact ? "small" : "medium"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Humidity Section */}
        <Grid item xs={12} width="100%">
          <Card variant="outlined" sx={{ mb: 2, width: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Humidity
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} width="45%">
                  <TextField
                    fullWidth
                    label="Snow Humidity (Doser int)"
                    type="number"
                    value={weatherData.snow_humidity_doser ?? ''}
                    onChange={handleIntegerChange('snow_humidity_doser')}
                    size={compact ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} width="45%">
                  <TextField
                    fullWidth
                    label="Air Humidity (% rH)"
                    type="number"
                    value={weatherData.air_humidity_rh ?? ''}
                    onChange={handleNumericChange('air_humidity_rh')}
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                    size={compact ? "small" : "medium"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Atmospheric Conditions Section */}
        <Grid item xs={12} width="100%">
          <Card variant="outlined" sx={{ mb: 2, width: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Atmospheric Conditions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <TextField
                    fullWidth
                    label="Clouds (0-10 sky parts)"
                    type="number"
                    value={weatherData.clouds_sky_parts ?? ''}
                    onChange={handleIntegerChange('clouds_sky_parts')}
                    inputProps={{ min: 0, max: 10 }}
                    size={compact ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Visibility</InputLabel>
                    <Select
                      value={weatherData.visibility || ''}
                      label="Visibility"
                      onChange={handleChange('visibility')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {visibilityOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Wind</InputLabel>
                    <Select
                      value={weatherData.wind || ''}
                      label="Wind"
                      onChange={handleChange('wind')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {windOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Precipitation</InputLabel>
                    <Select
                      value={weatherData.precipitation || ''}
                      label="Precipitation"
                      onChange={handleChange('precipitation')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {precipitationOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Snow Classification Section */}
        <Grid item xs={12} width="100%">
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Snow Classification
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Artificial Snow</InputLabel>
                    <Select
                      value={weatherData.artificial_snow || ''}
                      label="Artificial Snow"
                      onChange={handleChange('artificial_snow')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {artificialSnowOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Natural Snow</InputLabel>
                    <Select
                      value={weatherData.natural_snow || ''}
                      label="Natural Snow"
                      onChange={handleChange('natural_snow')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {naturalSnowOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Grain Size</InputLabel>
                    <Select
                      value={weatherData.grain_size || ''}
                      label="Grain Size"
                      onChange={handleChange('grain_size')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {grainSizeOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Snow Humidity</InputLabel>
                    <Select
                      value={weatherData.snow_humidity || ''}
                      label="Snow Humidity"
                      onChange={handleChange('snow_humidity')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {snowHumidityOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Track Conditions Section */}
        <Grid item xs={12} width="100%">
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Track Conditions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Track Hardness</InputLabel>
                    <Select
                      value={weatherData.track_hardness || ''}
                      label="Track Hardness"
                      onChange={handleChange('track_hardness')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {trackHardnessOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Track Shininess</InputLabel>
                    <Select
                      value={weatherData.track_shininess || ''}
                      label="Track Shininess"
                      onChange={handleChange('track_shininess')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {trackShininessOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} width="45%">
                  <FormControl fullWidth size={compact ? "small" : "medium"}>
                    <InputLabel>Track Dirtiness</InputLabel>
                    <Select
                      value={weatherData.track_dirtiness || ''}
                      label="Track Dirtiness"
                      onChange={handleChange('track_dirtiness')}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {trackDirtinessOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default WeatherDataForm;